import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, LineChart, ChevronLeft, ChevronRight } from 'lucide-react';
import { emotionalTimeline } from '../../data/toolsData';

const TimelineTool = () => {
  const navigate = useNavigate();
  const [activePhase, setActivePhase] = useState(0);

  const phase = emotionalTimeline.phases[activePhase];

  const goToPrevious = () => {
    if (activePhase > 0) {
      setActivePhase(prev => prev - 1);
    }
  };

  const goToNext = () => {
    if (activePhase < emotionalTimeline.phases.length - 1) {
      setActivePhase(prev => prev + 1);
    }
  };

  return (
    <div className="min-h-screen py-12 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate('/ferramentas')}
          className="flex items-center gap-2 text-[#8B7D6B] hover:text-[#6B4E37] mb-8 transition-colors"
          data-testid="back-to-tools-btn"
        >
          <ArrowLeft size={20} />
          Voltar às ferramentas
        </button>

        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-2xl bg-[#E8D5D5]/30 flex items-center justify-center mx-auto mb-6">
            <LineChart size={32} className="text-[#6B4E37]" />
          </div>
          <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl text-[#2C2C2C] mb-4">
            {emotionalTimeline.title}
          </h1>
          <p className="text-lg text-[#6B4E37] max-w-xl mx-auto">
            {emotionalTimeline.subtitle}
          </p>
        </div>

        {/* Timeline Navigation */}
        <div className="relative mb-8">
          {/* Progress Bar */}
          <div className="h-2 bg-[#F5F3F0] rounded-full overflow-hidden">
            <div 
              className="h-full transition-all duration-500 rounded-full"
              style={{ 
                width: `${((activePhase + 1) / emotionalTimeline.phases.length) * 100}%`,
                backgroundColor: phase.color 
              }}
            />
          </div>

          {/* Phase Dots */}
          <div className="flex justify-between mt-4">
            {emotionalTimeline.phases.map((p, index) => (
              <button
                key={p.id}
                onClick={() => setActivePhase(index)}
                className={`flex flex-col items-center transition-all duration-300 ${
                  index === activePhase ? 'scale-110' : 'opacity-60 hover:opacity-100'
                }`}
                data-testid={`phase-dot-${index}`}
              >
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-lg transition-all duration-300 ${
                    index === activePhase ? 'shadow-lg' : ''
                  }`}
                  style={{ 
                    backgroundColor: index === activePhase ? p.color : `${p.color}50`,
                  }}
                >
                  {p.emoji}
                </div>
                <span className="text-xs text-[#8B7D6B] mt-2 hidden md:block max-w-[80px] text-center">
                  {p.period}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Phase Content */}
        <div 
          className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-sm animate-fade-in"
          key={phase.id}
          data-testid="phase-content"
        >
          {/* Phase Header */}
          <div className="text-center mb-8">
            <span className="text-5xl mb-4 block">{phase.emoji}</span>
            <h2 className="font-['Cormorant_Garamond'] text-3xl text-[#2C2C2C] mb-2">
              {phase.name}
            </h2>
            <div 
              className="inline-block px-4 py-1 rounded-full text-sm"
              style={{ backgroundColor: `${phase.color}30` }}
            >
              <span className="text-[#6B4E37]">{phase.period}</span>
            </div>
          </div>

          {/* Description */}
          <p className="text-[#6B4E37] text-center text-lg mb-8">
            {phase.description}
          </p>

          {/* Feelings */}
          <div className="mb-8">
            <h3 className="text-sm font-medium text-[#8B7D6B] uppercase tracking-wider mb-4">
              O que podem sentir
            </h3>
            <div className="flex flex-wrap gap-2">
              {phase.feelings.map((feeling, index) => (
                <span 
                  key={index}
                  className="px-4 py-2 rounded-full text-sm"
                  style={{ backgroundColor: `${phase.color}20` }}
                >
                  <span className="text-[#6B4E37]">{feeling}</span>
                </span>
              ))}
            </div>
          </div>

          {/* Challenges */}
          <div className="mb-8">
            <h3 className="text-sm font-medium text-[#8B7D6B] uppercase tracking-wider mb-4">
              Desafios comuns
            </h3>
            <ul className="space-y-2">
              {phase.challenges.map((challenge, index) => (
                <li key={index} className="flex items-start gap-3 text-[#6B4E37]">
                  <span 
                    className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                    style={{ backgroundColor: phase.color }}
                  />
                  {challenge}
                </li>
              ))}
            </ul>
          </div>

          {/* Advice */}
          <div 
            className="rounded-xl p-6 mb-8"
            style={{ backgroundColor: `${phase.color}15` }}
          >
            <h3 className="text-sm font-medium text-[#8B7D6B] uppercase tracking-wider mb-3">
              Conselho
            </h3>
            <p className="text-[#2C2C2C] font-medium">
              {phase.advice}
            </p>
          </div>

          {/* Normalizer */}
          <div className="bg-[#FAF9F7] border border-[#E8E4E0] rounded-xl p-6 text-center">
            <p className="text-[#6B4E37] italic">
              "{phase.normalizer}"
            </p>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center mt-8">
          <button
            onClick={goToPrevious}
            disabled={activePhase === 0}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 ${
              activePhase === 0
                ? 'opacity-30 cursor-not-allowed'
                : 'bg-white/60 hover:bg-white/80 text-[#6B4E37]'
            }`}
            data-testid="prev-phase-btn"
          >
            <ChevronLeft size={20} />
            <span className="hidden sm:inline">Fase anterior</span>
          </button>

          <span className="text-sm text-[#8B7D6B]">
            {activePhase + 1} de {emotionalTimeline.phases.length}
          </span>

          <button
            onClick={goToNext}
            disabled={activePhase === emotionalTimeline.phases.length - 1}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 ${
              activePhase === emotionalTimeline.phases.length - 1
                ? 'opacity-30 cursor-not-allowed'
                : 'bg-white/60 hover:bg-white/80 text-[#6B4E37]'
            }`}
            data-testid="next-phase-btn"
          >
            <span className="hidden sm:inline">Próxima fase</span>
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Bottom Note */}
        <div className="mt-12 text-center">
          <p className="text-sm text-[#8B7D6B] max-w-lg mx-auto">
            Estas fases são baseadas em experiências reais de centenas de casais. 
            Não é uma ciência exacta - cada casal tem o seu próprio ritmo. 
            Use como guia, não como expectativa.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TimelineTool;
